import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';

interface NavigationAwareErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * PHASE 4: Navigation-Aware Error Boundary
 * Automatically resets error state when route changes
 * Prevents errors from persisting across navigation
 * Logs errors silently without blocking UI
 */
export const NavigationAwareErrorBoundary: React.FC<NavigationAwareErrorBoundaryProps> = ({ 
  children, 
  fallback 
}) => {
  const location = useLocation();
  const [resetKey, setResetKey] = useState(0);

  // PHASE 4: Reset error boundary when location changes
  useEffect(() => {
    setResetKey(prev => prev + 1);
  }, [location.pathname]);

  // PHASE 4: Silent error handling - log but don't show UI
  const handleReset = () => {
    console.log('[NavigationAwareErrorBoundary] Silent reset triggered');
    setResetKey(prev => prev + 1);
  };

  // Silent error boundary - no UI shown, just logging
  return (
    <ErrorBoundary 
      key={resetKey}
      fallback={fallback || null} // No fallback UI by default
      onReset={handleReset}
    >
      {children}
    </ErrorBoundary>
  );
};
